import React from 'react';
import { Tooltip, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import { isWipePlus } from '../lib/wipePlus';

interface UserShape {
  wipe_plus_until?: string | null;
}

interface Props {
  user: UserShape | null | undefined;
  /** Inline icon size — default 14px to sit beside username text */
  size?: number;
  /** Whether the badge links to /store. Defaults true. */
  linkToStore?: boolean;
}

/**
 * WIPE+ badge — small monochrome pixel "+" mark that sits next to a
 * subscriber's username everywhere their identity appears.
 *
 * Renders as a Nokia-aesthetic pixel square: a 14px box with `+` glyph,
 * 1px border, currentColor. No emoji, no neon. Tooltip says "WIPE+
 * subscriber" and (if linkToStore) clicking goes to /store.
 *
 * If the user isn't Wipe+ this returns null — safe to drop into any
 * username row unconditionally.
 */
function WipePlusBadge({ user, size = 14, linkToStore = true }: Props) {
  if (!isWipePlus(user)) return null;

  const inner = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        fontFamily: '"VT323", monospace',
        fontSize: `${Math.round(size * 0.85)}px`,
        lineHeight: 1,
        fontWeight: 700,
        border: '1px solid currentColor',
        borderRadius: 0,
        ml: 0.5,
        verticalAlign: 'middle',
        color: 'inherit',
        cursor: linkToStore ? 'pointer' : 'default',
        flexShrink: 0,
        // Subtle drop shadow so the badge "lifts" off the post bg
        boxShadow: '1px 1px 0 0 rgba(0,0,0,0.2)',
      }}
      aria-label="Wipe Plus subscriber"
    >
      +
    </Box>
  );

  return (
    <Tooltip title="WIPE+ subscriber — supports the site, gets per-action time-cost customization, badge across the app." arrow>
      {linkToStore ? (
        <Box component={Link} to="/store" sx={{ display: 'inline-flex', color: 'inherit', textDecoration: 'none' }}>
          {inner}
        </Box>
      ) : (
        inner
      )}
    </Tooltip>
  );
}

export default WipePlusBadge;
